import React from 'react';
import { observable } from 'mobx';
import { observer } from 'mobx-react';
import { SettingsBillingWrapper } from '@seedlang/hoc';
import { AppUI } from '@seedlang/state';
import { isPresent } from '@seedlang/utils';
import styled from '@emotion/styled';
import Text from 'components/text';
import Button from 'components/button/button';
import { Theme } from '@seedlang/constants';
import OutlineButton from 'components/button/outline_button';
import SettingsBillingMigrationSection from './settings_billing_migration_section';
import Spinner from 'components/spinner';
import { flexLeftColumn } from '@seedlang/style_mixins';
import { computed } from 'mobx';
import PageTitle from 'components/common/page_title';
import SettingsPatreon from './settings_patreon';
import Stack from 'components/layout/stack';
import WithdrawalModal from 'components/modal/withdrawal_modal';

const formatMembershipTitle = title => title?.replace(/Member$/, 'Membership');

const Wrapper = styled.div`
  ${flexLeftColumn()};
  align-items: stretch;
  gap: 20px;
`;

const Plan = styled.div`
  font-weight: bold;
`;

const Date = styled.div`
  font-size: 14px;
`;

const Cancelled = styled.span`
  color: red;
`;

const TableCellContent = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 20px;
`;

const TableCellLeft = styled.div`
  flex: 1;
`;

const ButtonRow = styled.div`
  display: flex;
  column-gap: 20px;
  row-gap: 5px;
  @media (max-width: 1150px) {
    flex-direction: column;
  }
`;

export const UndoLinkButton = styled.button`
  color: ${Theme.darkestBlue};
  cursor: pointer;
  text-decoration: underline;
  font-weight: inherit;
  background: none;
  border: none;
  padding: 0;
  font-size: inherit;
  &:hover {
    opacity: 0.8;
  }
`;


export const SmallSpinner = ({ background }) => (
  <Spinner
    width='50px'
    height='10px'
    background={background || 'currentColor'}
    margin='0'
  />
);

@observer
class SettingsBilling extends React.Component {
  @observable paymentAndInvoicesPortalLoading = false;
  @observable portalLoadingForSubscription = null;
  @observable withdrawalSubscription = null;

  @computed get hasTrialingSubscription() {
    return this.props.userSubscriptions?.some(sub => sub.isTrialing) || false;
  }
  onManagePaymentMethod = async () => {
    this.paymentAndInvoicesPortalLoading = true;
    try {
      window.location.href = await AppUI.stripeStore.getCustomerPortalUrl({ return_url: window.location.href });
    } catch {
      this.paymentAndInvoicesPortalLoading = false;
    }
  }
  onOpenSubscriptionPortal = async (subscription, actionType) => {
    this.portalLoadingForSubscription = subscription.id;
    try {
      window.location.href = await AppUI.stripeStore.getCustomerPortalUrl({
        return_url: window.location.href,
        action_type: actionType,
        stripe_account: subscription.stripeAccount,
        stripe_customer_id: subscription.stripeCustomerId,
        stripe_subscription_id: subscription.stripeSubscriptionId,
      });
    } catch {
      this.portalLoadingForSubscription = null;
    }
  }

  shouldShowCancelButton(subscription) {
    return !subscription.cancelAt &&
      (subscription.isActive || subscription.isTrialing);
  }

  onWithdrawn = () => {
    this.withdrawalSubscription = null;
    AppUI.userSubscriptionStore.getIndex({}, resp => {
      AppUI.userSubscriptionStore.afterGetIndex(resp);
    });
    AppUI.loadUser(null, { forceRefresh: true });
  }

  @computed get completedPaymentMigration() {
    return AppUI.user.paymentMigration?.status === 'completed';
  }

  render() {
    if (!AppUI.userIsLoaded || AppUI.userSubscriptionStore.hasOutstandingRequests) {
      return (
        <Wrapper>
          <PageTitle>Your Membership On Our Platform</PageTitle>
          <Text heading='3'>Your Membership On Our Platform</Text>
          <Spinner />
        </Wrapper>
      );
    }

    return (
      <Wrapper>
        <PageTitle>Your Membership On Our Platform</PageTitle>
        <Text heading='2'>Manage Membership</Text>
        <Text heading='3'>Your Membership On Our Platform</Text>
        {
          !AppUI.user.isMember &&
          <Text bold>You don't have an active subscription.</Text>
        }
        {
          AppUI.user.currentUserGroup?.freeMembership &&
          <div>
            You currently have a free <strong>{formatMembershipTitle(AppUI.user.currentGroupMembershipType?.name)}</strong>
            {AppUI.user.currentUserGroup.freeMembershipUntil?.date
              ? ` until ${AppUI.user.currentUserGroup.freeMembershipUntil?.formattedDateWithYear}`
              : '.'}
          </div>
        }
        {
          AppUI.user.isMember && this.props.activeSubscription &&
          <>
            {
              isPresent(this.props.subscriptionStatusText) &&
              <Text bold>
                <Stack gap='xs'>
                  {this.props.subscriptionStatusText}
                  {
                    isPresent(this.props.activeSubscription?.nextPhaseMembership?.title) &&
                      (this.portalLoadingForSubscription === this.props.activeSubscription?.id
                        ? <SmallSpinner background={Theme.darkestBlue} />
                        : <UndoLinkButton onClick={() => this.onOpenSubscriptionPortal(this.props.activeSubscription, 'release')}>
                            Cancel change
                          </UndoLinkButton>
                      )
                  }
                </Stack>
              </Text>
            }
          </>
        }
        {
          this.props.userSubscriptions.filter(item => item.isActive || item.isTrialing || item.pending).map(item => (
            <TableCellContent key={item.id}>
              <TableCellLeft>
                <Plan>
                  {item.membership?.title?.replace(/<[^>]*>/g, '')}
                </Plan>
                <Date>
                  {
                    isPresent(item.trialEnd) && item.trialEnd.isFuture
                      ? `Not charged until ${item.trialEnd.formattedDateWithYear}, then renews ${item.membership?.numberMonths === 12 ? 'yearly' : 'monthly'}`
                      : `${item.currentPeriodStart.formattedDateWithYear} - ${item.pending ? 'Pending' : item.currentPeriodEnd.formattedDateWithYear}`
                  }
                  {
                    item.cancelAt &&
                    <Cancelled> (Cancelled)</Cancelled>
                  }
                </Date>
              </TableCellLeft>
              {
                item.cancelAt && item.paymentMethod === 'stripe' && !this.hasTrialingSubscription && item.currentPeriodEnd?.isFuture &&
                <Button
                  disabled={this.portalLoadingForSubscription === item.id}
                  background={AppUI.site.accentColor || Theme.blue}
                  onClick={() => this.onOpenSubscriptionPortal(item, 'reactivate')}
                  minWidth='125px'
                >
                  {this.portalLoadingForSubscription === item.id
                    ? <SmallSpinner />
                    : 'Reactivate'
                  }
                </Button>
              }
              {
                this.shouldShowCancelButton(item) &&
                <OutlineButton
                  disabled={this.portalLoadingForSubscription === item.id}
                  selectedBackground={Theme.red}
                  minWidth='120px'
                  minHeight='40px'
                  onClick={() => this.onOpenSubscriptionPortal(item, 'cancel')}
                >
                  {
                    this.portalLoadingForSubscription === item.id
                      ? <SmallSpinner />
                      : <><i className='fa fa-times' style={{ marginRight: 4, marginTop: 2 }} />Cancel</>
                  }
                </OutlineButton>
              }
            </TableCellContent>
          ))
        }
        <ButtonRow>
          {
            (!AppUI.isPaymentMigrationInProgress && !AppUI.isPaymentMigrationStartable) && !this.props.activeSubscription?.oneTimePayment &&
            <Button
              to='memberships.index'
              background={AppUI.site.accentColor}
            >
              Update Your Membership Tier
            </Button>
          }
          {
            this.props.userSubscriptions.some(sub => sub.paymentMethod === 'stripe') &&
            <Button
              background={AppUI.site.accentColor || Theme.blue}
              onClick={this.onManagePaymentMethod}
              minWidth='216px'
            >
              {this.paymentAndInvoicesPortalLoading
                ? <SmallSpinner />
                : 'Payment & Invoices'
              }
            </Button>
          }
          {
            this.props.activeSubscription?.withdrawalEligible &&
            <OutlineButton
              selectedBackground={Theme.red}
              minWidth='216px'
              minHeight='40px'
              onClick={() => this.withdrawalSubscription = this.props.activeSubscription}
            >
              Withdraw from Contract
            </OutlineButton>
          }
        </ButtonRow>
        {
          AppUI.paymentMigrationEnabled &&
          <SettingsBillingMigrationSection hasActiveSubscription={this.props.activeSubscription?.membership} />
        }
        {
          (AppUI.site.patreonClientId && !this.props.activeSubscription?.membership && !this.completedPaymentMigration) &&
          <SettingsPatreon />
        }
        {
          this.withdrawalSubscription &&
          <WithdrawalModal
            subscription={this.withdrawalSubscription}
            onClose={() => this.withdrawalSubscription = null}
            onWithdrawn={this.onWithdrawn}
          />
        }
      </Wrapper>
    );
  }
}

export default SettingsBillingWrapper(SettingsBilling);
